<template>
  <div ref="componentModalDetail">
    <fullscreen-modal :show="show" @close="show = false" ref="modal">
      <div class="container">
        <div class="row">
          <div class="component-image">
            <img
              :src="this.$imageBase + component.image"
              :alt="getComponentName(component)"
            />
          </div>
          <div class="component-description">
            <span class="h3">{{ getComponentName(component) }}</span>
            <span v-if="isPhaseIn(component.phaseIn)" class="h3 phase-in">
              [new]
            </span>
            <span v-if="isPhaseOut(component.phaseOut)" class="h3 phase-out">
              [obsolete]
            </span>
            <span v-if="isPreCr(component.phaseIn)" class="h3 pre-cr">
              [coming soon]</span
            >
            <span v-if="component.isArchived" class="h3 is-archived">
              [archived]</span
            >
          </div>
        </div>
        <div class="row spec-table-container">
          <table>
            <tr v-for="property in properties" :key="property.name">
              <td class="table-key">{{ property.name }}</td>
              <td
                class="table-value"
                v-html="renderValueByName(property.name)"
              ></td>
            </tr>
          </table>
        </div>
        <div v-if="type !== 'emergency-driver'" class="row graph-container">
          <h4>Graph</h4>
        </div>
        <div v-if="type !== 'emergency-driver'" class="row">
          <easy-charts
            :type="type"
            :module="module"
            :driver="driver"
            :region="region"
          ></easy-charts>
          <div class="col">
            <ModuleLegend v-if="type === 'module'" :module="module" />
            <DriverLegend v-else />
          </div>
        </div>
      </div>

      <template v-slot:stickyfootercontent>
        <div class="component-modal-footer">
          <div class="modal-buttons">
            <div class="row component-button-container">
              <input
                type="button"
                class="btn btn-outline-secondary"
                value="Cancel"
                @click="closeModal"
              />
              <div class="spacer"></div>
              <input
                v-if="!multiLengthView"
                type="submit"
                class="btn btn-secondary"
                value="Add to current solution"
                @click="componentSelectedContinue(false)"
              />
              <input
                v-if="!multiLengthView"
                type="submit"
                class="btn btn-secondary"
                value="Add to new solution"
                @click="componentSelectedContinue(true)"
              />
            </div>
          </div>
        </div>
      </template>
    </fullscreen-modal>
  </div>
</template>

<script>
import { get } from "lodash";
import numeral from "numeral";
import { mapState } from "vuex";
import ModuleLegend from "@/components/legends/ModuleLegend";
import DriverLegend from "@/components/legends/DriverLegend";
import { isPhaseIn, isPhaseOut, isPreCr } from "@/utils/Lifecycle";

export default {
  name: "ComponentDetailModal",
  components: { DriverLegend, ModuleLegend },
  props: ["multiLengthView"],
  data() {
    return {
      show: true,
      isPhaseIn,
      isPhaseOut,
      isPreCr,
      shownProperties: {
        module: [
          {
            name: "12nc",
            key: "nc12",
          },
          {
            name: "Datasheet",
            key: "datasheetUrl",
            formatter: (val) => {
              return `<a href="${val}">datasheet </a>`;
            },
          },
          {
            name: "Application",
            key: "familyLevelOne",
          },
          {
            name: "Flux (lm)",
            key: "calculationResult.outputFluxOfArray",
            formatter: (val) => {
              return Math.round(val);
            },
          },
          {
            name: "Rth hs-a",
            key: "calculationResult.rthHeatsinkToAmbient",
            formatter: (val) => {
              return numeral(val).format("0.00");
            },
          },
          {
            name: "Current (mA)",
            key: "calculationResult.operatingCurrent",
            formatter: (val) => {
              return Math.round(val * 1000);
            },
          },
          {
            name: "Efficacy (lm/W)",
            key: "calculationResult.luminousEfficacyOfModule",
            formatter: (val) => {
              return Math.round(val);
            },
          },
          {
            name: "Tc (°C)",
            key: "calculationResult.temperature",
            formatter: (val) => {
              return Math.round(val);
            },
          },
          {
            name: "Modules in series",
            key: "numberOfModulesInSeries",
          },
          {
            name: "Modules in parallel",
            key: "numberOfModulesInParallel",
          },
          {
            name: "Voltage (V)",
            key: "calculationResult.outputVoltageOfArray",
            formatter: (val) => {
              return numeral(val).format("0.0");
            },
          },
          {
            name: "Power (W)",
            key: "calculationResult.power",
            formatter: (val) => {
              return numeral(val).format("0.0");
            },
          },
          {
            name: "Thermal power (W)",
            key: "calculationResult.thermalPower",
            formatter: (val) => {
              if (val < 10) {
                return numeral(val).format("0.0");
              }
              return Math.round(val);
            },
          },
          {
            name: "LES diameter (mm)",
            key: "housing.dia",
          },
          {
            name: "Length module (mm)\t",
            key: "housing.length",
          },
          {
            name: "Width module (mm)",
            key: "housing.width",
          },
          {
            name: "Height module (mm)",
            key: "housing.height",
          },
        ],
        driver: [
          {
            name: "12nc",
            key: "nc12",
          },
          {
            name: "Datasheet",
            key: "datasheetUrl",
            formatter: (val) => {
              return `<a href="${val}">datasheet </a>`;
            },
          },
          {
            name: "P out (W) ",
            key: "driverCalculationResult.outputPower",
          },
          {
            name: "I out (mA)",
            key: "driverCalculationResult.outputCurrent",
            formatter: (val) => {
              return Math.round(val * 1000);
            },
          },
          {
            name: "V out (V)",
            key: "driverCalculationResult.outputVoltage",
          },
          {
            name: "P out max (W) ",
            key: "opMax",
          },
          {
            name: "I out min (mA) ",
            key: "ocMin",
            formatter: (val) => {
              return Math.round(val * 1000);
            },
          },
          {
            name: "I out max (mA) ",
            key: "ocMax",
            formatter: (val) => {
              return Math.round(val * 1000);
            },
          },
          {
            name: "V out min (V) ",
            key: "ovMin",
          },
          {
            name: "V out max (V)",
            key: "ovMax",
          },
          {
            name: "Set output current",
            key: "driverCalculationResult.outputCurrent",
            formatter: (val) => {
              return Math.round(val * 1000);
            },
          },
          {
            name: "Dimming ",
            key: "dimmingTypes",
            formatter: (array) => {
              return array.map((item) => item.name).join(". ");
            },
          },
          {
            name: "Eff. at full load - (%) at max input voltage ",
            key: "eff",
          },
          {
            name: "Eff. at operating point at 230Vac (%)",
            key: "mains.230",
          },
          {
            name: "Isolation",
            key: "isolationClass.name",
          },
          {
            name: "Mounting",
            key: "mountingTypes",
            formatter: (array) => {
              return array.map((item) => item.name).join(". ");
            },
          },
          {
            name: "Length (mm)",
            key: "housing.length",
          },
          {
            name: "Width (mm)",
            key: "housing.width",
          },
          {
            name: "Height (mm)",
            key: "housing.height",
          },
        ],
        emergencyDriver: [
          {
            name: "12nc",
            key: "nc12",
          },
          {
            name: "Datasheet",
            key: "datasheetUrl",
            formatter: (val) => {
              return `<a href="${val}">datasheet </a>`;
            },
          },
          {
            name: "P out max (W) ",
            key: "opMax",
          },
          {
            name: "I out max (mA) ",
            key: "ocMax",
            formatter: (val) => {
              return Math.round(val * 1000);
            },
          },
          {
            name: "I in  max (mA) ",
            key: "ibodineMaxIn",
          },
          {
            name: "V out min (V) ",
            key: "ovMin",
          },
          {
            name: "V out max (V)",
            key: "ovMax",
          },
          {
            name: "Length (inch)",
            key: "housing.length",
          },
          {
            name: "Width (inch)",
            key: "housing.width",
          },
          {
            name: "Height (inch)",
            key: "housing.height",
          },
        ],
      },
    };
  },
  computed: {
    ...mapState("brain", ["region"]),
    ...mapState("brain/global", { type: "activeComponent" }),
    ...mapState("brain/modules", {
      module: "module",
    }),
    ...mapState("brain/drivers", {
      driver: "driver",
    }),
    ...mapState("brain/emergencyDrivers", {
      emergencyDriver: "emergencyDriver",
    }),
    propertyType() {
      let type = this.type;
      if (type === "emergency-driver") {
        type = "emergencyDriver";
      }
      return type;
    },

    properties() {
      return this.shownProperties[this.propertyType].filter((item) => {
        // Check for ACL specific columns
        if (item.key === "calculationResult.rthHeatsinkToAmbient") {
          return this.canI("RTH_MODULE_CALCULATIONS");
        }
        return true;
      });
    },
    component() {
      if (this.type === "module") {
        return this.module;
      } else if (this.type === "emergency-driver") {
        return this.emergencyDriver;
      } else {
        return this.driver;
      }
    },
  },
  created() {
    window.addEventListener("keydown", this.onArrowKeyPress, false);
  },
  methods: {
    onArrowKeyPress(event) {
      if (event.key === "ArrowUp") {
        event.preventDefault();
        this.$emit("onArrowKeyPress", { type: this.type, direction: "up" });
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        this.$emit("onArrowKeyPress", { type: this.type, direction: "down" });
      } else if (event.key === "Enter") {
        event.preventDefault();
        this.componentSelectedContinue();
      }
    },
    renderValueByName(name) {
      const target = this.shownProperties[this.propertyType].find(
        (obj) => obj.name === name
      );
      const value = get(this.component, target.key);
      if (!value) {
        return "-";
      }
      return target.formatter ? target.formatter(value) : value;
    },
    componentSelectedContinue(addNew = false) {
      this.$emit("componentSelectedContinue", {
        name: this.component.name,
        type: this.type,
        data: this.component,
        addNew: addNew,
      });
      this.closeModal();
    },
    closeModal() {
      this.$refs.modal._hide();
    },
    getComponentName(component) {
      if (this.type === "driver") {
        return this.region === "na" ? component.marketingName : component.name;
      }
      return component.name;
    },
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onArrowKeyPress, true);
  },
};
</script>

<style lang="scss" scoped>
.component-image {
  width: 30%;
  padding: 0 30px 0 0;

  img {
    height: 100%;
    width: 100%;
    max-height: 250px;
    max-width: 250px;
  }
}

.datasheet-link {
  display: block;
  margin-bottom: 30px;
}

.spec-table-container {
  table {
    margin: 40px 0;
    width: 100%;

    td {
      padding: 5px 0;
    }
  }

  .table-key {
    width: 30%;
    font-weight: bold;
  }
}

.speclist-item {
  // font-size: 16px;
  margin-bottom: 5px;

  .key {
    display: inline-block;
    color: #bbbbbb; //handpicked because none of the styleguide colors worked well.
  }

  .value {
    margin-left: 5px;
    display: inline-block;
  }
}

.graph-container {
  h4 {
    margin-bottom: 0;
  }
}

.container {
  padding: 0 0 100px 0;
}

.component-modal-footer {
  .modal-buttons {
    width: 100%;

    //safari only css for responsiveness fix
    @media not all and (min-resolution: 0.001dpcm) {
      @supports (-webkit-appearance: none) {
        max-height: 31px;
      }
    }
  }

  .component-button-container {
    justify-content: flex-end;

    input {
      margin: 0 12px;
    }
  }

  .pin-module-container {
    margin-top: 16px;
    justify-content: flex-end;
  }

  .spacer {
    flex-grow: 2;
  }
}

.component {
  display: flex;
  flex-flow: column wrap;
  justify-content: flex-start;
  width: 70%;

  .component-selected {
    color: $blue;
    font-family: $font-family-sans-serif;
    font-weight: $btn-font-weight;
  }
}

.phase-out {
  color: #e98500;
}

.phase-in {
  color: #00bfff;
}

.pre-cr {
  color: #32cd32;
  white-space: nowrap;
}

.is-archived {
  color: #a900ff;
  white-space: nowrap;
}
</style>
